// Contact form with real-time validation and better UX

import { saveFormData, getFormData, clearFormData } from './storage.js';

export const initContactForm = () => {
  const form = document.getElementById('cform');
  if (!form) return;

  const RECIPIENT = 'hello@futuresee.you';
  const nameEl = document.getElementById('cf-name');
  const emailEl = document.getElementById('cf-email');
  const phoneEl = document.getElementById('cf-phone');
  const msgEl = document.getElementById('cf-msg');
  const submitBtn = form.querySelector('.csubmit');
  const note = document.getElementById('cnote');

  // Restore form data on init
  const saved = getFormData();
  if (saved) {
    nameEl.value = saved.name || '';
    emailEl.value = saved.email || '';
    phoneEl.value = saved.phone || '';
    msgEl.value = saved.message || '';
  }

  // Validation rules
  const validators = {
    name: (val) => {
      const trimmed = val.trim();
      if (!trimmed) return { valid: false, msg: 'Name is required' };
      if (trimmed.length < 2) return { valid: false, msg: 'Name too short' };
      if (trimmed.length > 100) return { valid: false, msg: 'Name too long' };
      return { valid: true };
    },
    email: (val) => {
      const trimmed = val.trim();
      if (!trimmed) return { valid: false, msg: 'Email is required' };
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(trimmed)) {
        return { valid: false, msg: 'Invalid email address' };
      }
      return { valid: true };
    },
    phone: (val) => {
      const trimmed = val.trim();
      if (!trimmed) return { valid: false, msg: 'Phone number is required' };
      if (trimmed.length < 5) return { valid: false, msg: 'Phone number too short' };
      if (trimmed.length > 20) return { valid: false, msg: 'Phone number too long' };
      return { valid: true };
    },
    message: (val) => {
      const trimmed = val.trim();
      if (!trimmed) return { valid: false, msg: 'Message is required' };
      if (trimmed.length < 10) return { valid: false, msg: 'Message too short (min 10 chars)' };
      if (trimmed.length > 5000) return { valid: false, msg: 'Message too long (max 5000 chars)' };
      return { valid: true };
    },
  };

  // Validate single field
  const validateField = (el, fieldName) => {
    const result = validators[fieldName](el.value);
    const fieldWrapper = el.closest('.cfield');
    const errorBox = fieldWrapper?.querySelector('.cfield-error');

    if (result.valid) {
      el.classList.remove('err');
      if (errorBox) {
        errorBox.textContent = '';
        errorBox.classList.remove('active');
      }
      return true;
    } else {
      el.classList.add('err');
      if (errorBox) {
        errorBox.textContent = result.msg;
        errorBox.classList.add('active');
      }
      return false;
    }
  };

  // Real-time validation and save on input
  nameEl.addEventListener('input', () => {
    validateField(nameEl, 'name');
    saveFormData(nameEl.value, emailEl.value, phoneEl.value, msgEl.value);
  });
  emailEl.addEventListener('input', () => {
    validateField(emailEl, 'email');
    saveFormData(nameEl.value, emailEl.value, phoneEl.value, msgEl.value);
  });
  phoneEl.addEventListener('input', () => {
    validateField(phoneEl, 'phone');
    saveFormData(nameEl.value, emailEl.value, phoneEl.value, msgEl.value);
  });
  msgEl.addEventListener('input', () => {
    validateField(msgEl, 'message');
    saveFormData(nameEl.value, emailEl.value, phoneEl.value, msgEl.value);
  });

  // Blur validation (better UX)
  nameEl.addEventListener('blur', () => validateField(nameEl, 'name'));
  emailEl.addEventListener('blur', () => validateField(emailEl, 'email'));
  phoneEl.addEventListener('blur', () => validateField(phoneEl, 'phone'));
  msgEl.addEventListener('blur', () => validateField(msgEl, 'message'));

  // Form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // Validate all fields
    const nameValid = validateField(nameEl, 'name');
    const emailValid = validateField(emailEl, 'email');
    const phoneValid = validateField(phoneEl, 'phone');
    const msgValid = validateField(msgEl, 'message');

    if (!nameValid || !emailValid || !phoneValid || !msgValid) {
      note.className = 'cnote bad';
      note.textContent = 'Please fix the errors above';
      return;
    }

    // Remove active state immediately on mobile
    submitBtn.blur();
    submitBtn.style.pointerEvents = 'none';

    // Show loading state
    submitBtn.disabled = true;
    const originalText = submitBtn.innerHTML;
    submitBtn.innerHTML = '<span style="opacity: 0.7;">Sending…</span>';
    note.className = 'cnote';
    note.textContent = '';

    // Send form data via API
    setTimeout(async () => {
      try {
        const response = await fetch('/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: nameEl.value.trim(),
            email: emailEl.value.trim(),
            phone: phoneEl.value.trim(),
            message: msgEl.value.trim(),
          }),
        });

        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }

        // Reset form
        form.reset();
        clearFormData();
        [nameEl, emailEl, phoneEl, msgEl].forEach(el => {
          el.classList.remove('err');
          const errorBox = el.closest('.cfield')?.querySelector('.cfield-error');
          if (errorBox) {
            errorBox.textContent = '';
            errorBox.classList.remove('active');
          }
        });

        // Show success
        note.className = 'cnote ok';
        note.textContent = '✓ Message sent! I\'ll get back to you soon.';

        // Reset button
        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.innerHTML = originalText;
          submitBtn.style.pointerEvents = 'auto';
          submitBtn.removeAttribute('style');
          note.textContent = '';
        }, 2000);
      } catch (error) {
        console.error('Form submission error:', error);
        note.className = 'cnote bad';
        note.textContent =
          'Error sending message. Please try again later.';

        // Reset button
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
        submitBtn.style.pointerEvents = 'auto';
        submitBtn.removeAttribute('style');
      }
    }, 600);
  });
};
